import React, { useEffect, useMemo, useState } from 'react';
import { useFitStore } from '../store/useFitStore';
import { supabase } from '../lib/supabase';
import type { Appointment } from '../store/types';
import { expandOccurrences } from '../lib/ics';
import AppointmentEditor from '../components/coach/AppointmentEditor';

/* ---------- date helpers (local-time, no UTC drift) ---------- */
const startOfWeek = (d: Date): Date => {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // Monday = 0
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
};
const startOfDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const addDays = (d: Date, n: number): Date => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const isoLocal = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const fmtShort = (d: Date): string => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const fmtDayHeading = (d: Date): string => d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
const fmtTime = (d: Date): string => d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

type Mode = '4w' | 'week' | 'day';
const MODE_LABELS: Record<Mode, string> = { '4w': '4 weeks', week: 'Week', day: 'Day' };
interface Occ { appt: Appointment; start: Date; end: Date }

type SyncSummary = { added: number; updated: number; removed: number; unmatched: number; matched: number };

const CoachCalendar: React.FC = () => {
  const { user, appointments, reloadAppointments, showToast } = useFitStore();
  const [mode, setMode] = useState<Mode>('4w');
  const [offset, setOffset] = useState(0); // in `mode` units from today
  const [editor, setEditor] = useState<{ appt: Appointment; isNew: boolean; occurrenceStartIso?: string } | null>(null);

  /* ---------- Google Calendar sync (secret iCal address) ---------- */
  const [gcalOpen, setGcalOpen] = useState(false);
  const [gcal, setGcal] = useState<{ connected: boolean; syncedAt?: string } | null>(null);
  const [gcalUrl, setGcalUrl] = useState('');
  const [gcalBusy, setGcalBusy] = useState(false);

  const summarize = (s: SyncSummary) =>
    `${s.added} added · ${s.updated} updated · ${s.removed} removed${s.unmatched ? ` · ${s.unmatched} didn’t match a client` : ''}`;

  const gcalCall = async (body: Record<string, unknown>): Promise<any> => {
    const { data, error } = await supabase.functions.invoke('calendar-sync', { body });
    if (error) return null;
    return data;
  };

  // Quiet auto-sync on page load — the function itself skips when synced <12h ago.
  useEffect(() => {
    let on = true;
    (async () => {
      const res = await gcalCall({ action: 'sync' });
      if (!on || !res) return;
      if (res.ok) setGcal({ connected: true, syncedAt: res.syncedAt });
      else if (res.reason === 'not_connected') setGcal({ connected: false });
      const s = res.summary as SyncSummary | undefined;
      if (res.ok && s && s.added + s.updated + s.removed > 0) {
        await reloadAppointments();
        showToast(`Google Calendar synced — ${summarize(s)}`);
      }
    })();
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runGcal = async (body: Record<string, unknown>) => {
    setGcalBusy(true);
    const res = await gcalCall(body);
    setGcalBusy(false);
    if (!res) { showToast('Sync failed — try again', 'error'); return; }
    if (!res.ok) {
      const msg = res.reason === 'bad_url'
        ? 'That doesn’t look like a Google secret iCal address.'
        : res.reason === 'feed_unreachable'
          ? 'Couldn’t read the feed — re-copy the secret address and try again.'
          : 'Sync failed — try again.';
      showToast(msg, 'error');
      return;
    }
    setGcal({ connected: body.action !== 'disconnect', syncedAt: res.syncedAt });
    if (body.action === 'disconnect') { showToast('Google Calendar disconnected'); return; }
    if (res.summary) {
      await reloadAppointments();
      showToast(`Synced — ${summarize(res.summary)}`);
      setGcalOpen(false);
    }
  };

  const today = new Date();
  const todayIso = isoLocal(today);

  // Visible window: 4 Monday-anchored weeks, one week, or a single day.
  const { winStart, days } = useMemo(() => {
    if (mode === '4w' || mode === 'week') {
      const span = mode === '4w' ? 28 : 7;
      const start = addDays(startOfWeek(today), offset * span);
      return { winStart: start, days: Array.from({ length: span }, (_, i) => addDays(start, i)) };
    }
    const start = addDays(startOfDay(today), offset);
    return { winStart: start, days: [start] };
  }, [mode, offset, todayIso]); // eslint-disable-line react-hooks/exhaustive-deps

  const winEnd = useMemo(() => {
    const end = new Date(days[days.length - 1]);
    end.setHours(23, 59, 59, 999);
    return end;
  }, [days]);

  // Expand every appointment over the window, then bucket occurrences by local day.
  const byDay = useMemo(() => {
    const map = new Map<string, Occ[]>();
    for (const appt of appointments) {
      for (const o of expandOccurrences(appt, winStart, winEnd)) {
        const key = isoLocal(o.start);
        const arr = map.get(key) || [];
        arr.push({ appt, start: o.start, end: o.end });
        map.set(key, arr);
      }
    }
    for (const arr of map.values()) arr.sort((a, b) => a.start.getTime() - b.start.getTime());
    return map;
  }, [appointments, winStart, winEnd]);

  const rangeLabel = mode === 'day' ? fmtDayHeading(days[0]) : `${fmtShort(days[0])} – ${fmtShort(days[days.length - 1])}`;

  const blankAppt = (day: Date): Appointment => {
    const start = new Date(day);
    start.setHours(9, 0, 0, 0);
    const end = new Date(start.getTime() + 30 * 60000);
    return {
      id: crypto.randomUUID(),
      coachId: user?.id || '',
      title: '',
      startsAt: start.toISOString(),
      endsAt: end.toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      status: 'scheduled',
      exdate: [],
      createdAt: new Date().toISOString(),
    };
  };

  const total = useMemo(() => Array.from(byDay.values()).reduce((n, a) => n + a.length, 0), [byDay]);

  // Thin "+ Add" row for days/weeks with nothing scheduled — keeps long windows skimmable.
  const addRow = (day: Date, label: React.ReactNode) => (
    <div key={isoLocal(day)} className="flex items-center justify-between border-b border-fit-line pb-2">
      {label}
      <button onClick={() => setEditor({ appt: blankAppt(day), isNew: true })} className="text-fit-muted hover:text-white text-[13px]">+ Add</button>
    </div>
  );

  const dayBlock = (day: Date, occ: Occ[]) => {
    const key = isoLocal(day);
    return (
      <div key={key}>
        <div className="flex items-center justify-between mb-2">
          <h2 className={`text-[13px] font-semibold ${key === todayIso ? 'text-white' : 'text-fit-text-secondary'}`}>
            {fmtDayHeading(day)}{key === todayIso && <span className="ml-2 text-[11px] text-fit-muted">Today</span>}
          </h2>
          <button onClick={() => setEditor({ appt: blankAppt(day), isNew: true })} className="text-fit-muted hover:text-white text-[13px]">+ Add</button>
        </div>
        <div className="flex flex-col gap-2">
          {occ.map((o) => {
            const canceled = o.appt.status === 'canceled';
            return (
              <button
                key={`${o.appt.id}-${o.start.toISOString()}`}
                onClick={() => setEditor({ appt: o.appt, isNew: false, occurrenceStartIso: o.start.toISOString() })}
                className="card text-left flex items-start gap-3 hover:border-fit-line-strong transition-colors"
                style={{ padding: '12px 14px', opacity: canceled ? 0.5 : 1 }}
              >
                <div className="text-[12px] font-semibold tabular-nums text-fit-text-secondary shrink-0" style={{ minWidth: 96 }}>
                  {fmtTime(o.start)}<br /><span className="text-fit-muted">{fmtTime(o.end)}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">
                    {o.appt.title || o.appt.attendeeName || 'Appointment'}
                    {o.appt.rrule && <span className="ml-2 text-[11px] text-fit-muted" title="Repeats">⟳</span>}
                    {o.appt.status === 'completed' && <span className="ml-2 text-[11px] text-fit-muted">✓ done</span>}
                    {o.appt.status === 'no_show' && <span className="ml-2 text-[11px] text-fit-muted">no-show</span>}
                  </p>
                  <p className="text-[12px] text-fit-text-secondary truncate">
                    {o.appt.attendeeName && o.appt.title ? o.appt.attendeeName : ''}
                    {o.appt.attendeeName && o.appt.title && o.appt.location ? ' · ' : ''}
                    {o.appt.location || ''}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="main-content">
      <header data-tour="calendar" className="flex items-start justify-between gap-3 mb-4">
        <div>
          <p className="mono-label">Schedule</p>
          <h1 className="text-2xl font-bold tracking-tight">Calendar</h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setGcalOpen(true)}
            className="btn btn-glass btn-sm"
            title="Pull your Google Calendar into FitFlex, mapped to your clients"
          >
            ⇅ Google{gcal?.connected ? ' ✓' : ''}
          </button>
          <button onClick={() => setEditor({ appt: blankAppt(mode === 'day' ? days[0] : today), isNew: true })} className="btn btn-primary btn-sm">
            + New appointment
          </button>
        </div>
      </header>

      {/* controls */}
      <div className="flex items-center justify-between gap-2 flex-wrap mb-5">
        <div className="flex items-center gap-2">
          <button onClick={() => setOffset(0)} className="btn btn-glass btn-sm">Today</button>
          <div className="flex items-center gap-1">
            <button onClick={() => setOffset((o) => o - 1)} className="btn btn-glass btn-sm" aria-label="Previous">‹</button>
            <button onClick={() => setOffset((o) => o + 1)} className="btn btn-glass btn-sm" aria-label="Next">›</button>
          </div>
          <span className="text-[14px] font-semibold">{rangeLabel}</span>
        </div>
        <div className="flex items-center gap-1 bg-fit-bg-tertiary rounded-full p-0.5">
          {(['4w', 'week', 'day'] as Mode[]).map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setOffset(0); }}
              className={`px-3 py-1 rounded-full text-[12px] font-semibold transition-colors ${
                mode === m ? 'bg-white text-fit-ink' : 'text-fit-text-secondary hover:text-white'
              }`}
            >
              {MODE_LABELS[m]}
            </button>
          ))}
        </div>
      </div>

      {total === 0 && mode !== '4w' && (
        <div className="card text-center" style={{ padding: 'var(--spacing-xl)' }}>
          <p className="font-semibold mb-1">No appointments {mode === 'week' ? 'this week' : 'this day'}</p>
          <p className="text-fit-text-secondary text-[13px]">Schedule client sessions, lead consults, and recurring check-ins here.</p>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {mode === '4w'
          ? Array.from({ length: 4 }, (_, w) => days.slice(w * 7, w * 7 + 7)).map((week) => {
              // One header per week; only days with appointments get a full block.
              const busy = week
                .map((d) => ({ d, occ: byDay.get(isoLocal(d)) || [] }))
                .filter((x) => x.occ.length > 0);
              return (
                <div key={isoLocal(week[0])} className="flex flex-col gap-3">
                  <p className="mono-label">Week of {fmtShort(week[0])}</p>
                  {busy.length > 0
                    ? busy.map((x) => dayBlock(x.d, x.occ))
                    : addRow(week[0], <span className="text-[13px] text-fit-muted">No appointments</span>)}
                </div>
              );
            })
          : days.map((day) => {
              const key = isoLocal(day);
              const occ = byDay.get(key) || [];
              if (mode === 'week' && occ.length === 0) {
                // keep the week skimmable: render empty days as a thin add-row
                return addRow(
                  day,
                  <span className={`text-[13px] ${key === todayIso ? 'text-white font-semibold' : 'text-fit-muted'}`}>{fmtDayHeading(day)}</span>
                );
              }
              return dayBlock(day, occ);
            })}
      </div>

      {editor && (
        <AppointmentEditor
          appt={editor.appt}
          isNew={editor.isNew}
          occurrenceStartIso={editor.occurrenceStartIso}
          onClose={() => setEditor(null)}
        />
      )}

      {/* ---------- Google Calendar sync setup ---------- */}
      {gcalOpen && (
        <div className="modal-overlay" onClick={() => !gcalBusy && setGcalOpen(false)}>
          <div className="modal-content" style={{ maxWidth: 540 }} onClick={(e) => e.stopPropagation()}>
            <div className="card flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-xl font-bold tracking-tight">Sync Google Calendar</h2>
                <button onClick={() => !gcalBusy && setGcalOpen(false)} className="text-fit-text-secondary hover:text-white text-lg leading-none">×</button>
              </div>

              {gcal?.connected ? (
                <>
                  <p className="text-[13px] text-fit-text-secondary leading-relaxed">
                    Connected. Sessions on your Google Calendar that match a client (by guest email or
                    their name in the title) appear here automatically — recurring schedules included.
                    {gcal.syncedAt && (
                      <span className="block mt-1 text-fit-muted text-[12px]">
                        Last synced {new Date(gcal.syncedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </span>
                    )}
                  </p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => runGcal({ action: 'sync', force: true })} disabled={gcalBusy} className="btn btn-primary btn-sm">
                      {gcalBusy ? 'Syncing…' : 'Sync now'}
                    </button>
                    <button onClick={() => runGcal({ action: 'disconnect' })} disabled={gcalBusy} className="text-[12px] text-fit-muted hover:text-white underline underline-offset-2">
                      Disconnect
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[13px] text-fit-text-secondary leading-relaxed">
                    Paste your calendar's <span className="text-white font-semibold">secret iCal address</span> and your
                    existing schedule flows in, mapped to your clients — no re-typing recurring sessions.
                    Only events matching a client on your roster are imported.
                  </p>
                  <ol className="text-[12px] text-fit-text-secondary flex flex-col gap-1 list-decimal pl-4">
                    <li>Open <span className="text-white">Google Calendar → Settings</span> (gear icon)</li>
                    <li>Pick your calendar on the left → <span className="text-white">Integrate calendar</span></li>
                    <li>Copy <span className="text-white">"Secret address in iCal format"</span> and paste it below</li>
                  </ol>
                  <input
                    value={gcalUrl}
                    onChange={(e) => setGcalUrl(e.target.value)}
                    placeholder="https://calendar.google.com/calendar/ical/…/basic.ics"
                    className="input w-full"
                  />
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[11px] text-fit-muted">The address stays private to your account — you can disconnect anytime.</p>
                    <button
                      onClick={() => runGcal({ action: 'connect', url: gcalUrl.trim() })}
                      disabled={gcalBusy || !gcalUrl.trim()}
                      className="btn btn-primary btn-sm shrink-0"
                    >
                      {gcalBusy ? 'Syncing…' : 'Connect & sync'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachCalendar;
